import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Loader2, User, Lock, UserPlus, MessageCircle, Shield, Gift, Globe } from 'lucide-react';

const LoginPage = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    name: ''
  });
  const { login, setupAdmin, needsSetup } = useAuth();
  const { t, language, setLanguage, languageNames, languageFlags, availableLanguages } = useLanguage();
  const navigate = useNavigate();

  const WHATSAPP_NUMBER = '+963984107429';
  const WHATSAPP_LINK = `https://wa.me/963984107429?text=${encodeURIComponent('مرحباً، أريد إنشاء حساب في نظام إدارة المخبز')}`;
  // QR Code API for WhatsApp
  const QR_CODE_URL = `https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(WHATSAPP_LINK)}`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (needsSetup) {
        // First time setup - create admin
        if (!formData.name.trim()) {
          toast.error('الرجاء إدخال اسم المدير');
          setLoading(false);
          return;
        }
        await setupAdmin(formData.username, formData.password, formData.name);
        toast.success('تم إنشاء حساب المدير بنجاح');
      } else {
        await login(formData.username, formData.password);
        toast.success('تم تسجيل الدخول بنجاح');
      }
      navigate('/');
    } catch (error) {
      console.error('Auth error:', error);
      if (error.response?.status === 401) {
        toast.error('اسم المستخدم أو كلمة المرور غير صحيحة');
      } else if (error.response?.status === 400) {
        toast.error(error.response?.data?.detail || 'حدث خطأ');
      } else {
        toast.error('حدث خطأ، الرجاء المحاولة مرة أخرى');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* Language Selector - Fixed Position */}
      <div className="fixed top-4 end-4 z-50">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="gap-2 bg-white/90 backdrop-blur" data-testid="login-language-selector">
              <Globe className="w-4 h-4" />
              <span>{languageFlags[language]}</span>
              <span>{languageNames[language]}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-40">
            {availableLanguages.map((lang) => (
              <DropdownMenuItem 
                key={lang} 
                onClick={() => setLanguage(lang)}
                className={`gap-2 ${language === lang ? 'bg-amber-50 text-amber-700' : ''}`}
              >
                <span>{languageFlags[lang]}</span>
                <span>{languageNames[lang]}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Hero Image */}
      <div 
        className="h-[30vh] md:h-auto md:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1691862329594-4eb279f01bc1?crop=entropy&cs=srgb&fm=jpg&q=85')`
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-l from-stone-900/60 to-transparent" />
        <div className="absolute bottom-6 start-6 end-6 md:bottom-12 md:start-12 text-white">
          <h1 className="text-2xl md:text-4xl font-bold mb-2">{t('appName')}</h1>
          <p className="text-sm md:text-lg text-white/80">
            {language === 'ar' ? 'إدارة طلبات الخبز والحسابات بكل سهولة' :
             language === 'de' ? 'Brotbestellungen und Konten einfach verwalten' :
             language === 'tr' ? 'Ekmek siparişlerini ve hesapları kolayca yönetin' :
             language === 'ku' ? 'Rêvebiriya spartin û hesabên nan bi hêsanî' :
             'Manage bread orders and accounts easily'}
          </p>
        </div>
      </div>

      {/* Login Form */}
      <div className="flex-1 md:w-1/2 flex items-center justify-center p-6 md:p-12 bg-stone-50">
        <div className="w-full max-w-md space-y-4">
          {/* Trial Banner */}
          {!needsSetup && (
            <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white p-4 rounded-xl shadow-lg text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <Gift className="w-5 h-5" />
                <span className="font-bold">{t('freeTrial')}</span>
              </div>
              <p className="text-sm text-emerald-100">{t('freeTrialDesc')}</p>
            </div>
          )}

          <Card className="border-stone-200 shadow-lg">
            <CardHeader className="space-y-1 text-center">
              <div className="mx-auto w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <CardTitle className="text-2xl font-bold text-stone-900">
                {t('loginTitle')}
              </CardTitle>
              <CardDescription className="text-stone-500">
                {t('enterUsername')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {needsSetup && (
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-stone-700">{t('ownerName')}</Label>
                    <div className="relative">
                      <UserPlus className="absolute start-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                      <Input
                        id="name"
                        data-testid="setup-name-input"
                        type="text"
                        placeholder="أدخل اسمك"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="ps-10 h-11 text-right"
                        dir="rtl"
                      />
                    </div>
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="username" className="text-stone-700">{t('username')}</Label>
                  <div className="relative">
                    <User className="absolute start-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                    <Input
                      id="username"
                      data-testid="username-input"
                      type="text"
                      placeholder={t('enterUsername')}
                      value={formData.username}
                      onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                      className="ps-10 h-11"
                      required
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-stone-700">{t('password')}</Label>
                  <div className="relative">
                    <Lock className="absolute start-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                    <Input
                      id="password"
                      data-testid="password-input"
                      type="password"
                      placeholder={t('enterPassword')}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="ps-10 h-11"
                      required
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  data-testid="login-submit-btn"
                  className="w-full h-12 bg-amber-600 hover:bg-amber-700 text-white font-bold text-lg rounded-lg transition-all active:scale-95"
                  disabled={loading}
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    t('loginButton')
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Contact Admin Section */}
          {!needsSetup && (
            <Card className="border-stone-200 bg-gradient-to-br from-green-50 to-emerald-50">
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  {/* QR Code */}
                  <div className="flex-shrink-0">
                    <img 
                      src={QR_CODE_URL} 
                      alt="WhatsApp QR Code" 
                      className="w-24 h-24 rounded-lg border-2 border-green-200 bg-white p-1"
                    />
                  </div>
                  
                  {/* Contact Info */}
                  <div className="flex-1">
                    <h3 className="font-bold text-stone-900 mb-1">{t('noAccount')}</h3>
                    <p className="text-sm text-stone-600 mb-3">
                      {t('contactAdmin')}
                    </p>
                    <a 
                      href={WHATSAPP_LINK}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-bold transition-colors"
                    >
                      <MessageCircle className="w-4 h-4" />
                      {t('contactViaWhatsapp')}
                    </a>
                    <p className="text-xs text-stone-500 mt-2 font-mono" dir="ltr">{WHATSAPP_NUMBER}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Security Notice */}
          {!needsSetup && (
            <div className="flex items-center justify-center gap-2 text-stone-500 text-sm">
              <Shield className="w-4 h-4 text-green-600" />
              <span>{t('dataSecure')}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
